import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Filter, RefreshCw, Loader2, MapPin, CalendarClock, Building2, X, Sparkles, Globe,
  ChevronLeft, ChevronRight, Bot, ThumbsUp, ThumbsDown, Star, Ban, Route, Gauge, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import { api, brl, fmtDate, errMsg } from "@/lib/api";
import { ScoutBadge, StatusOpBadge, StageBadge, Chip, ComplianceBadge } from "@/components/Badges";
import OpportunityDrawer from "@/components/OpportunityDrawer";

const MODALIDADES = [
  { id: 6, nome: "Pregão Eletrônico" }, { id: 8, nome: "Dispensa" },
  { id: 4, nome: "Concorrência Eletrônica" }, { id: 9, nome: "Inexigibilidade" },
  { id: 12, nome: "Credenciamento" }, { id: 3, nome: "Concurso" },
];
const UFS = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];
const FAIXAS = [
  { label: "Até R$ 10 mil", min: 0, max: 10000 },
  { label: "R$ 10 mil – 50 mil", min: 10000, max: 50000 },
  { label: "R$ 50 mil – 100 mil", min: 50000, max: 100000 },
  { label: "R$ 100 mil – 500 mil", min: 100000, max: 500000 },
  { label: "Acima de R$ 500 mil", min: 500000, max: null },
];
const STATUS_TABS = [
  { key: "", label: "Todas" },
  { key: "aberta", label: "Abertas" },
  { key: "julgamento", label: "Em julgamento" },
  { key: "encerrada", label: "Encerradas" },
];
const SCOUT_CLASSES = [
  ["prioritaria", "🔥 Prioritária"], ["boa", "🟢 Boa"], ["analisar", "🟡 Analisar"],
  ["ressalvas", "⚠️ Ressalvas"], ["baixa", "🔴 Baixa"],
];
const NIVEIS3 = [["baixa", "Baixa"], ["media", "Média"], ["alta", "Alta"]];
const RISCOS = [["baixo", "Baixo"], ["moderado", "Moderado"], ["alto", "Alto"]];
const DESCARTE_MOTIVOS = ["Produto", "Distância", "Margem", "Documentação", "Prazo", "Fornecedor", "Complexidade", "Logística", "Outro"];
const DIST_CHIP = {
  proximo: ["Próximo", "bg-emerald-50 text-emerald-700"],
  moderado: ["Distância moderada", "bg-sky-50 text-sky-700"],
  distante: ["Distante", "bg-amber-50 text-amber-700"],
  desfavoravel: ["Logística desfavorável", "bg-orange-50 text-orange-700"],
  nao_identificado: ["Local ND", "bg-slate-100 text-slate-500"],
};
const COMPLEX_CHIP = {
  baixa: ["Complex. baixa", "bg-emerald-50 text-emerald-700"],
  media: ["Complex. média", "bg-amber-50 text-amber-700"],
  alta: ["Complex. alta", "bg-rose-50 text-rose-700"],
};

export default function Radar() {
  const [sp, setSp] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");
  const [reanalyzing, setReanalyzing] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selId, setSelId] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [batchLoading, setBatchLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [discardFor, setDiscardFor] = useState(null);
  const [raio, setRaio] = useState(null);
  const pageSize = 48;

  const [q, setQ] = useState(sp.get("q") || "");
  const [ufs, setUfs] = useState([]);
  const [mods, setMods] = useState([]);
  const [faixa, setFaixa] = useState(null);
  const [order, setOrder] = useState("scout");
  const [statusOp, setStatusOp] = useState("");
  const [scoutClass, setScoutClass] = useState([]);
  const [complexidade, setComplexidade] = useState([]);
  const [risco, setRisco] = useState([]);
  const [aderNivel, setAderNivel] = useState([]);
  const [minIndice, setMinIndice] = useState(0);
  const [ocultarDesc, setOcultarDesc] = useState(true);

  useEffect(() => { api.get("/company").then((r) => setRaio(r.data?.scout_settings?.raio_km || 500)).catch(() => setRaio(500)); }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { order, page, page_size: pageSize };
      if (q) params.q = q;
      if (ufs.length) params.uf = ufs.join(",");
      if (mods.length) params.modalidade = mods.join(",");
      if (faixa != null) {
        if (FAIXAS[faixa].min != null) params.valor_min = FAIXAS[faixa].min;
        if (FAIXAS[faixa].max != null) params.valor_max = FAIXAS[faixa].max;
      }
      if (statusOp) params.status_op = statusOp;
      if (scoutClass.length) params.scout_class = scoutClass.join(",");
      if (complexidade.length) params.complexidade = complexidade.join(",");
      if (risco.length) params.risco = risco.join(",");
      if (aderNivel.length) params.aderencia_nivel = aderNivel.join(",");
      if (minIndice) params.min_indice = minIndice;
      if (ocultarDesc) params.ocultar_descartadas = true;
      const { data } = await api.get("/opportunities", { params });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }, [q, ufs, mods, faixa, order, statusOp, scoutClass, complexidade, risco, aderNivel, minIndice, ocultarDesc, page]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setQ(sp.get("q") || ""); }, [sp]);
  useEffect(() => { setPage(1); }, [q, ufs, mods, faixa, order, statusOp, scoutClass, complexidade, risco, aderNivel, minIndice, ocultarDesc]);

  const runSync = async (payload, label) => {
    setSyncing(true);
    try {
      await api.post("/radar/sync", payload);
      toast.info(label);
      const poll = setInterval(async () => {
        try {
          const { data } = await api.get("/radar/sync-status");
          setSyncMsg(data.running ? `${data.imported || 0} novas · ${data.scanned || 0} lidas${data.total_ufs ? ` · ${data.ufs_done || 0}/${data.total_ufs} UFs` : ""}` : "");
          if (!data.running) {
            clearInterval(poll); setSyncing(false); setSyncMsg("");
            toast.success(`PNCP sincronizado: ${data.imported || 0} novas, ${data.updated || 0} atualizadas`);
            fetchData();
          }
        } catch { /* keep polling */ }
      }, 3000);
    } catch (e) { toast.error(errMsg(e)); setSyncing(false); }
  };
  const sync = () => { const u = ufs.length ? ufs : ["RN"]; runSync({ ufs: u }, `Sincronizando PNCP (${u.join(", ")})…`); };
  const syncBrasil = () => runSync({ brasil: true }, "Sincronizando o Brasil inteiro (27 estados)…");

  const reanalisar = async () => {
    setReanalyzing(true);
    try {
      await api.post("/radar/scout/reanalisar");
      toast.info("🤖 SCOUT reanalisando as oportunidades…");
      const poll = setInterval(async () => {
        try {
          const { data } = await api.get("/radar/scout/status");
          if (!data.running) {
            clearInterval(poll); setReanalyzing(false);
            toast.success(`SCOUT concluído: ${data.processed || 0} oportunidades analisadas`);
            fetchData();
          }
        } catch { /* keep polling */ }
      }, 3000);
    } catch (e) { toast.error(errMsg(e)); setReanalyzing(false); }
  };

  const saveRaio = async (v) => {
    setRaio(v);
    try { await api.put("/company", { scout_settings: { raio_km: v } }); toast.success(`Raio preferencial: ${v === 99999 ? "Nacional" : v + " km"} — reanalise para aplicar`); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const sendFeedback = async (oid, decisao, motivo) => {
    try {
      const { data } = await api.post(`/opportunities/${oid}/scout/feedback`, { decisao, motivo });
      setItems((arr) => arr.map((x) => (x.id === oid ? data : x)));
      const msg = { interesse: "👍 Marcado como interesse", nao_interesse: "👎 Marcado sem interesse", favoritar: "⭐ Favoritada", descartar: "🚫 Descartada" };
      toast.success(msg[decisao] || "Registrado");
      if (decisao === "descartar" && ocultarDesc) fetchData();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const toggle = (arr, set, v) => set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  const toggleSelect = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const batchAnalyze = async () => {
    setBatchLoading(true);
    try {
      const ids = Array.from(selected).slice(0, 6);
      const { data } = await api.post("/opportunities/analyze-batch", { ids });
      toast.success(`${data.analisadas} oportunidade(s) analisada(s) pela IA`);
      setSelected(new Set()); fetchData();
    } catch (e) { toast.error(errMsg(e)); } finally { setBatchLoading(false); }
  };
  const clearFilters = () => {
    setUfs([]); setMods([]); setFaixa(null); setQ(""); setSp({});
    setStatusOp(""); setScoutClass([]); setComplexidade([]); setRisco([]); setAderNivel([]); setMinIndice(0);
  };

  const renderPagination = (pos) => {
    if (!(total > pageSize && !loading && items.length > 0)) return null;
    const totalPages = Math.ceil(total / pageSize);
    const go = (p) => { const np = Math.min(Math.max(1, p), totalPages); setPage(np); window.scrollTo({ top: 0, behavior: "smooth" }); };
    const from = Math.max(1, page - 2);
    const to = Math.min(totalPages, from + 4);
    const nums = [];
    for (let i = from; i <= to; i++) nums.push(i);
    return (
      <div data-testid={`radar-pagination-${pos}`} className={`flex flex-col items-center gap-3 ${pos === "top" ? "mb-5" : "mt-8"}`}>
        <span data-testid={`radar-page-indicator-${pos}`} className="text-xs font-semibold text-slate-500">Página {page} de {totalPages}</span>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <button data-testid={`radar-page-prev-${pos}`} disabled={page <= 1} onClick={() => go(page - 1)} className="flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"><ChevronLeft className="h-4 w-4" /> Anterior</button>
          {nums.map((n) => (<button key={n} data-testid={`radar-page-${pos}-${n}`} onClick={() => go(n)} className={`rounded-lg px-3.5 py-2 text-sm font-semibold ${n === page ? "bg-sky-600 text-white" : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>{n}</button>))}
          <button data-testid={`radar-page-next-${pos}`} disabled={page >= totalPages} onClick={() => go(page + 1)} className="flex items-center gap-1 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-40">Próxima <ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>
    );
  };

  const CheckGroup = ({ title, options, value, setValue }) => (
    <div>
      <label className="mb-1 block text-xs font-semibold text-slate-600">{title}</label>
      <div className="flex flex-wrap gap-1">
        {options.map(([v, l]) => (
          <button key={v} onClick={() => toggle(value, setValue, v)} className={`rounded px-2 py-1 text-[11px] font-semibold ${value.includes(v) ? "bg-sky-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{l}</button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="animate-fade-up pb-28">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-display text-2xl font-bold text-slate-900"><Bot className="h-6 w-6 text-sky-600" /> Radar de Oportunidades · SCOUT</h1>
          <p className="text-sm text-slate-500">{total.toLocaleString("pt-BR")} oportunidades — fonte oficial PNCP{syncMsg ? ` · sincronizando: ${syncMsg}` : ""}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="radar-filter-toggle" onClick={() => setShowFilters((v) => !v)} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 lg:hidden"><Filter className="h-4 w-4" /> Filtros</button>
          <button data-testid="radar-reanalisar-btn" onClick={reanalisar} disabled={reanalyzing} className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-sky-600 to-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60">{reanalyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bot className="h-4 w-4" />} Reanalisar SCOUT</button>
          <button data-testid="radar-sync-btn" onClick={sync} disabled={syncing} className="flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60">{syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Sincronizar {ufs.length ? ufs.join(",") : "RN"}</button>
          <button data-testid="radar-sync-brasil-btn" onClick={syncBrasil} disabled={syncing} className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60">{syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Globe className="h-4 w-4" />} Brasil inteiro</button>
        </div>
      </div>

      {/* Atalhos de status */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {STATUS_TABS.map((t) => (
          <button key={t.key} data-testid={`radar-status-tab-${t.key || "todas"}`} onClick={() => setStatusOp(t.key)}
            className={`rounded-full px-4 py-1.5 text-xs font-bold ${statusOp === t.key ? "bg-sky-600 text-white" : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>{t.label}</button>
        ))}
        <span className="mx-1 h-5 w-px bg-slate-200" />
        <label className="flex items-center gap-1.5 text-xs font-medium text-slate-600"><input type="checkbox" checked={ocultarDesc} onChange={(e) => setOcultarDesc(e.target.checked)} className="h-3.5 w-3.5 rounded" /> Ocultar descartadas</label>
      </div>

      <div className="flex gap-5">
        {/* Filters */}
        <aside className={`${showFilters ? "fixed inset-0 z-40 overflow-y-auto bg-white p-5 lg:static lg:z-auto lg:bg-transparent lg:p-0" : "hidden"} w-full shrink-0 lg:block lg:w-72`}>
          <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between">
              <span className="font-display font-semibold text-slate-800">Filtros SCOUT</span>
              <div className="flex items-center gap-2">
                <button onClick={clearFilters} className="text-xs font-semibold text-sky-600">Limpar</button>
                <button className="lg:hidden text-slate-400" onClick={() => setShowFilters(false)}><X className="h-4 w-4" /></button>
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600">Buscar no objeto</label>
              <input data-testid="radar-filter-input-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="papel, toner, uniforme..." className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sky-400" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600">Índice Scout mínimo: {minIndice}</label>
              <input data-testid="radar-filter-indice" type="range" min="0" max="100" step="5" value={minIndice} onChange={(e) => setMinIndice(Number(e.target.value))} className="w-full accent-sky-600" />
            </div>
            <CheckGroup title="Classificação Scout" options={SCOUT_CLASSES} value={scoutClass} setValue={setScoutClass} />
            <CheckGroup title="Aderência" options={NIVEIS3} value={aderNivel} setValue={setAderNivel} />
            <CheckGroup title="Complexidade" options={NIVEIS3} value={complexidade} setValue={setComplexidade} />
            <CheckGroup title="Risco" options={RISCOS} value={risco} setValue={setRisco} />
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600">Raio preferencial (logística)</label>
              <select data-testid="radar-raio-select" value={raio ?? 500} onChange={(e) => saveRaio(Number(e.target.value))} className="w-full rounded-lg border border-slate-200 px-2 py-2 text-sm">
                {[["50","Até 50 km"],["100","Até 100 km"],["200","Até 200 km"],["500","Até 500 km"],["99999","Nacional"]].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <p className="mt-1 text-[10px] text-slate-400">A distância nunca elimina uma oportunidade — apenas ajusta a prioridade.</p>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600">Faixa de valor</label>
              <div className="space-y-1">
                {FAIXAS.map((fx, i) => (<button key={i} onClick={() => setFaixa(faixa === i ? null : i)} className={`w-full rounded-lg px-3 py-1.5 text-left text-xs font-medium ${faixa === i ? "bg-sky-50 text-sky-700" : "text-slate-600 hover:bg-slate-50"}`}>{fx.label}</button>))}
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600">Modalidade</label>
              <div className="space-y-1">
                {MODALIDADES.map((m) => (<label key={m.id} className="flex cursor-pointer items-center gap-2 text-xs text-slate-600"><input type="checkbox" checked={mods.includes(m.id)} onChange={() => toggle(mods, setMods, m.id)} className="h-3.5 w-3.5 rounded text-sky-600" /> {m.nome}</label>))}
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600">Estados (UF)</label>
              <div className="flex flex-wrap gap-1">
                {UFS.map((u) => (<button key={u} onClick={() => toggle(ufs, setUfs, u)} className={`rounded px-2 py-1 text-[11px] font-semibold ${ufs.includes(u) ? "bg-sky-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{u}</button>))}
              </div>
            </div>
          </div>
        </aside>

        {/* Grid */}
        <div className="min-w-0 flex-1">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap gap-2 text-xs">
              {[["scout", "SCOUT (recomendado)"], ["indice", "Maior índice"], ["aderencia", "Maior aderência"], ["valor", "Maior valor"], ["recente", "Mais recentes"]].map(([v, l]) => (
                <button key={v} data-testid={`radar-order-${v}`} onClick={() => setOrder(v)} className={`rounded-lg px-3 py-1.5 font-semibold ${order === v ? "bg-slate-900 text-white" : "bg-white text-slate-600 border border-slate-200"}`}>{l}</button>
              ))}
            </div>
            {total > 0 && (<span data-testid="radar-pagination-info" className="text-xs font-medium text-slate-500">Mostrando {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} de {total.toLocaleString("pt-BR")}</span>)}
          </div>
          {renderPagination("top")}
          {loading ? (
            <div className="grid h-64 place-items-center"><Loader2 className="h-7 w-7 animate-spin text-sky-600" /></div>
          ) : items.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center">
              <p className="text-slate-500">Nenhuma oportunidade encontrada com esses filtros.</p>
              <button onClick={sync} className="mt-3 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white">Sincronizar PNCP agora</button>
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {items.map((o) => {
                const s = o.scout || {};
                const fb = o.scout_feedback?.decisao;
                const dist = DIST_CHIP[s.logistica?.nivel];
                const comp = COMPLEX_CHIP[s.complexidade?.nivel];
                return (
                  <div key={o.id} data-testid="opportunity-card" onClick={() => setSelId(o.id)} className="flex cursor-pointer flex-col rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition-all hover:border-sky-400 hover:shadow-md">
                    <div className="mb-2 flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <input type="checkbox" data-testid="opportunity-select" checked={selected.has(o.id)} onClick={(e) => e.stopPropagation()} onChange={() => toggleSelect(o.id)} className="h-4 w-4 rounded border-slate-300 text-sky-600" />
                        <ScoutBadge scout={s} />
                      </div>
                      <StatusOpBadge status={s.status_operacional} />
                    </div>
                    <div className="mb-1 flex items-center gap-1.5 text-xs font-medium text-slate-500"><Building2 className="h-3.5 w-3.5" /><span className="truncate">{o.orgao_razao_social}</span></div>
                    <p className="line-clamp-2 text-sm font-medium text-slate-800">{o.objeto}</p>

                    {(s.motivos || []).length > 0 && (
                      <div className="mt-2 rounded-lg bg-slate-50 p-2">
                        <div className="mb-1 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-slate-500"><Bot className="h-3 w-3" /> Por que essa oportunidade?</div>
                        <ul className="space-y-0.5">
                          {s.motivos.slice(0, 3).map((m, i) => (<li key={i} className="flex items-start gap-1 text-[11px] text-slate-600"><span className="text-sky-500">•</span><span className="line-clamp-1">{m}</span></li>))}
                        </ul>
                      </div>
                    )}

                    <div className="mt-2 flex flex-wrap gap-1">
                      {comp && <Chip className={comp[1]}>{comp[0]}</Chip>}
                      {dist && <Chip className={dist[1]}><Route className="mr-0.5 h-3 w-3" />{s.logistica?.distancia_km != null ? `${s.logistica.distancia_km} km` : dist[0]}</Chip>}
                      {s.risco?.nivel && <Chip className={s.risco.nivel === "alto" ? "bg-rose-50 text-rose-700" : s.risco.nivel === "moderado" ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}><ShieldAlert className="mr-0.5 h-3 w-3" />Risco {s.risco.nivel}</Chip>}
                      {o.compliance_status && <ComplianceBadge status={o.compliance_status} />}
                      {o.recorrencia?.is && <Chip className="bg-violet-100 text-violet-700"><span data-testid="radar-recorrente-badge" title={`${o.recorrencia.compras_orgao || 0} compra(s) anterior(es) do órgão · ${o.recorrencia.itens_uf || 0} itens semelhantes na UF`}>♻ Recorrente</span></Chip>}
                    </div>

                    <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3 text-xs text-slate-500">
                      <span className="font-display text-sm font-bold text-slate-900">{brl(o.valor_estimado)}</span>
                      <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{o.uf}</span>
                    </div>
                    <div className="mt-2 flex items-center justify-between text-[11px] text-slate-400">
                      <span className="flex items-center gap-1"><CalendarClock className="h-3 w-3" />{fmtDate(o.data_publicacao)}</span>
                      {o.saved && <StageBadge stage={o.stage} />}
                    </div>

                    {/* Feedback rápido */}
                    <div className="mt-3 flex items-center justify-between gap-1 border-t border-slate-100 pt-2" onClick={(e) => e.stopPropagation()}>
                      <button data-testid="scout-fb-interesse" title="Tenho interesse" onClick={() => sendFeedback(o.id, "interesse")} className={`flex-1 rounded-md py-1.5 ${fb === "interesse" ? "bg-emerald-100 text-emerald-700" : "text-slate-400 hover:bg-slate-100 hover:text-emerald-600"}`}><ThumbsUp className="mx-auto h-4 w-4" /></button>
                      <button data-testid="scout-fb-nao" title="Não tenho interesse" onClick={() => sendFeedback(o.id, "nao_interesse")} className={`flex-1 rounded-md py-1.5 ${fb === "nao_interesse" ? "bg-slate-200 text-slate-700" : "text-slate-400 hover:bg-slate-100 hover:text-slate-600"}`}><ThumbsDown className="mx-auto h-4 w-4" /></button>
                      <button data-testid="scout-fb-favoritar" title="Favoritar" onClick={() => sendFeedback(o.id, "favoritar")} className={`flex-1 rounded-md py-1.5 ${fb === "favoritar" ? "bg-amber-100 text-amber-600" : "text-slate-400 hover:bg-slate-100 hover:text-amber-500"}`}><Star className="mx-auto h-4 w-4" /></button>
                      <button data-testid="scout-fb-descartar" title="Descartar" onClick={() => setDiscardFor(o.id)} className={`flex-1 rounded-md py-1.5 ${fb === "descartar" ? "bg-rose-100 text-rose-600" : "text-slate-400 hover:bg-slate-100 hover:text-rose-500"}`}><Ban className="mx-auto h-4 w-4" /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {renderPagination("bottom")}
        </div>
      </div>

      {selId && <OpportunityDrawer id={selId} onClose={() => setSelId(null)} onChanged={fetchData} />}

      {discardFor && (
        <div className="fixed inset-0 z-50 grid place-items-center p-4">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setDiscardFor(null)} />
          <div className="relative w-full max-w-sm rounded-xl bg-white p-5 shadow-2xl" data-testid="scout-discard-modal">
            <h3 className="mb-1 font-display text-lg font-bold text-slate-900">Por que descartar?</h3>
            <p className="mb-3 text-xs text-slate-500">O SCOUT usa esse motivo para melhorar as próximas análises.</p>
            <div className="grid grid-cols-2 gap-2">
              {DESCARTE_MOTIVOS.map((m) => (
                <button key={m} data-testid={`scout-discard-${m}`} onClick={() => { sendFeedback(discardFor, "descartar", m); setDiscardFor(null); }} className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:border-rose-300 hover:bg-rose-50">{m}</button>
              ))}
            </div>
            <button onClick={() => setDiscardFor(null)} className="mt-3 w-full rounded-lg py-2 text-sm font-semibold text-slate-400 hover:text-slate-600">Cancelar</button>
          </div>
        </div>
      )}

      {selected.size > 0 && (
        <div className="fixed bottom-6 left-1/2 z-40 flex -translate-x-1/2 items-center gap-3 rounded-full border border-slate-200 bg-white px-5 py-3 shadow-xl">
          <span className="text-sm font-medium text-slate-700">{selected.size} selecionada(s){selected.size > 6 ? " (máx 6 por vez)" : ""}</span>
          <button data-testid="batch-analyze-btn" onClick={batchAnalyze} disabled={batchLoading} className="flex items-center gap-2 rounded-full bg-gradient-to-r from-sky-600 to-emerald-600 px-4 py-1.5 text-sm font-semibold text-white disabled:opacity-60">{batchLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Analisar com IA</button>
          <button onClick={() => setSelected(new Set())} className="text-sm text-slate-400 hover:text-slate-600">Limpar</button>
        </div>
      )}
    </div>
  );
}
